// HR reply composer for the candidate's WhatsApp thread (#3017). Lives in the
// drawer footer (next to the action buttons) so it's always visible on the
// Messages tab without scrolling. On a successful send it calls `onSent` so the
// drawer refetches the thread and the new `hr_reply` row appears.
import { useEffect, useState, type ReactNode } from "react";
import { AlertTriangle, Loader2, SendIcon } from "lucide-react";
import { Button } from "@component/button";
import { sendWhatsappReply, WhatsappReplyError } from "@service/whatsapp";
import { catchErrorLog } from "@util/sentry";

// WhatsApp only delivers free-form (non-template) messages within 24h of the
// candidate's last inbound message. Used for a proactive hint; the server's 409
// is still the authoritative check.
const WHATSAPP_FREEFORM_WINDOW_MS = 24 * 60 * 60 * 1000;

interface WhatsappReplyComposerProps {
    candidateId: string;
    /** created_at of the candidate's most recent inbound message, or null. */
    lastInboundAt: string | null;
    /** Called after a reply is sent so the drawer can refetch the thread. */
    onSent: () => void;
    /** Extra action rendered beside the send button (#3242: HR "Trigger Step").
        Also shown next to the blocked-composer banner — a closed 24h window is
        exactly when the trigger tool is needed. */
    trailingAction?: ReactNode;
}

export default function WhatsappReplyComposer({
    candidateId,
    lastInboundAt,
    onSent,
    trailingAction,
}: WhatsappReplyComposerProps) {
    const [draft, setDraft] = useState<string>("");
    const [sending, setSending] = useState<boolean>(false);
    const [sendError, setSendError] = useState<string | null>(null);
    // Set to a reason string when the composer is unusable (409 window closed /
    // 404 no opt-in) — the input is replaced with an explanation.
    const [composerBlocked, setComposerBlocked] = useState<string | null>(null);

    // Reset composer state when the drawer switches to another candidate.
    useEffect(() => {
        setDraft("");
        setSendError(null);
        setComposerBlocked(null);
    }, [candidateId]);

    const windowLikelyClosed =
        !lastInboundAt ||
        Date.now() - new Date(lastInboundAt).getTime() > WHATSAPP_FREEFORM_WINDOW_MS;

    async function handleSend() {
        const text = draft.trim();
        if (!text || sending || composerBlocked || windowLikelyClosed) return;
        setSending(true);
        setSendError(null);
        try {
            await sendWhatsappReply(candidateId, text);
            setDraft("");
            onSent();
        } catch (err) {
            if (err instanceof WhatsappReplyError) {
                setSendError(err.message);
                // A closed 24h window / missing opt-in won't clear on retry —
                // lock the composer and explain, per the ticket.
                if (err.kind === "window_closed" || err.kind === "no_optin") {
                    setComposerBlocked(err.message);
                }
            } else {
                setSendError("Couldn't send the reply. Please try again.");
            }
            catchErrorLog(`[whatsapp-reply] send failed: ${err}`);
        } finally {
            setSending(false);
        }
    }

    if (composerBlocked) {
        return (
            <div className="mb-3 flex items-start gap-2">
                <div className="flex-1 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 dark:bg-amber-950/30 dark:border-amber-900">
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700 dark:text-amber-300">{composerBlocked}</p>
                </div>
                {trailingAction}
            </div>
        );
    }

    return (
        <div className="mb-3">
            {/* WhatsApp only delivers free-form replies within 24h of the
                candidate's last message. Once that window has passed (or they've
                never messaged), keep the reply box visible but DISABLED, and
                explain why, rather than letting HR type a reply that would fail. */}
            {windowLikelyClosed && (
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-1.5">
                    You can't reply right now — WhatsApp only delivers replies within
                    24 hours of the candidate's last message.
                </p>
            )}
            {sendError && (
                <p className="text-xs text-rose-600 dark:text-rose-400 mb-1.5">
                    {sendError}
                </p>
            )}
            <div className="flex items-end gap-2">
                <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            void handleSend();
                        }
                    }}
                    rows={1}
                    disabled={sending || windowLikelyClosed}
                    placeholder="Reply to the candidate on WhatsApp…"
                    className="flex-1 resize-none max-h-32 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                />
                <Button
                    size="sm"
                    onClick={() => void handleSend()}
                    disabled={!draft.trim() || sending || windowLikelyClosed}
                    className="flex-shrink-0 h-9"
                    title={windowLikelyClosed ? "The 24-hour reply window has closed" : "Send reply"}
                >
                    {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <SendIcon className="w-4 h-4" />
                    )}
                </Button>
                {trailingAction}
            </div>
        </div>
    );
}
